import { toast } from "sonner";

// List of error codes that are typically caused by browser extensions
const BROWSER_EXTENSION_ERRORS = [
  'ERR_BLOCKED_BY_CLIENT',
  'Failed to fetch',
  'AppCheck: ReCAPTCHA error',
  'Failed to get document because the client is offline'
];

// App Check specific errors
const APP_CHECK_ERRORS = [
  'App attestation failed',
  'PERMISSION_DENIED',
  'appCheck/recaptcha-error',
  'appCheck/fetch-status-error'
];

// Check if error is App Check related
export const isAppCheckError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString();
  const errorCode = error.code || '';
  
  return APP_CHECK_ERRORS.some(err => 
    errorMessage.includes(err) || errorCode.includes(err)
  );
};

// Handle App Check errors with user-friendly messages
export const handleAppCheckError = (error: any, context?: string) => {
  if (isAppCheckError(error)) {
    console.warn('App Check error detected:', {
      error: error.message,
      code: error.code,
      context,
      timestamp: new Date().toISOString()
    });
    
    // Don't show toast for App Check errors - they're usually transient
    // or require admin configuration
    return true; // Error was handled
  }
  
  return false;
};

// Check if error is likely caused by browser extensions
export const isBrowserExtensionError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString();
  const errorUrl = error.url || '';
  
  // Check for blocked URLs and error messages
  return BROWSER_EXTENSION_ERRORS.some(err => 
    errorMessage.includes(err) || 
    errorUrl.includes('firestore.googleapis.com') ||
    errorUrl.includes('r.stripe.com') ||
    errorUrl.includes('sentry.io')
  );
};

// Handle Firebase errors with user-friendly messages
export const handleFirebaseError = (error: any, context?: string) => {
  if (isBrowserExtensionError(error)) {
    toast.error(
      'Network requests blocked by browser extension. Please disable ad blockers or privacy extensions for this site.',
      { duration: 5000, id: 'browser-extension-error' }
    );
    console.warn('Browser extension blocking detected:', {
      error: error.message,
      context,
      timestamp: new Date().toISOString()
    });
    return true; // Error was handled
  }
  
  // Log other errors normally
  console.error('Firebase error:', error);
  return false; // Error was not handled as extension error
};

// Retry configuration for failed requests
export const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  backoffMultiplier: 2
};

// Generic retry wrapper for Firebase operations
export const withRetry = async <T>(
  operation: () => Promise<T>,
  context?: string
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // If it's a browser extension error, don't retry
      if (isBrowserExtensionError(error)) {
        handleFirebaseError(error, context);
        throw error;
      }
      
      // If we've exhausted retries, throw the error
      if (attempt === RETRY_CONFIG.maxRetries) {
        throw error;
      }
      
      // Wait before retrying with exponential backoff
      const delay = RETRY_CONFIG.retryDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      console.warn(`Retrying operation (${attempt + 1}/${RETRY_CONFIG.maxRetries}):`, context);
    }
  }
  
  throw lastError;
};

// Initialize error handling for Firebase
export const initializeFirebaseErrorHandling = () => {
  // Override console.error to filter out repetitive extension errors
  const originalError = console.error;
  const errorCounts = new Map<string, number>();
  
  console.error = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    
    // Filter out repetitive browser extension errors
    if (isBrowserExtensionError({ message })) {
      const count = errorCounts.get(message) || 0;
      errorCounts.set(message, count + 1);
      
      // Only log the first 5 instances of each extension error
      if (count < 5) {
        originalError.apply(console, args);
      } else if (count === 5) {
        originalError('🔇 Suppressing further browser extension error messages...');
      }
    } else {
      originalError.apply(console, args);
    }
  };
  
  // Add global error handler for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (isBrowserExtensionError(event.reason)) {
      event.preventDefault();
      handleFirebaseError(event.reason, 'Unhandled promise rejection');
    }
  });
};
